import {useSelector} from 'react-redux';
import {useRef, useCallback} from 'react';
import {useWindowDimensions, View, StyleSheet} from 'react-native';
import {DrawerLayout} from 'extensions/viewport/DrawerLayout';
import {Outlet, useParams} from 'extensions/navigation';
import {Menu} from 'interface/stacks/Menu';
import {FileLoader} from 'interface/stacks/FileLoader';
import {FolderPicker} from 'interface/stacks/FolderPicker';
import {TransferUploads} from 'interface/stacks/TransferUploads';
import {TransferDownloads} from 'interface/stacks/TransferDownloads';
import {useDragDropTarget} from 'interface/hooks/useDragDropTarget';
import {DropFrame} from 'interface/base/DropFrame';
import {breakpoints} from 'config/styles';

import * as app from 'store/slices/app';
import * as files from 'store/slices/files';
import * as transfers from 'store/slices/transfers';

export function MyFiles() {
  const drawer = useRef<DrawerLayout>(null);
  const dndRef = useRef<View>(null);
  const screen = useWindowDimensions();
  const {folderKey} = useParams<"folderKey">();
  const parentFolder = useSelector(files.selectors.getItem(folderKey));
  const preview = useSelector(files.selectors.getPreview);
  const upload = useSelector(transfers.selectors.getUpload);
  const download = useSelector(transfers.selectors.getDownloadBulk);
  const isLoggedIn = useSelector(app.selectors.isLoggedIn);
  const isFixedMenu = isLoggedIn && screen.width > breakpoints.menu;
  const isDrawerEnabled = !isFixedMenu && isLoggedIn;

  const renderMenu = useCallback(() => isDrawerEnabled ? <Menu/> : null, [isDrawerEnabled]);
  const openMenu = useCallback(() => drawer.current?.openDrawer(), []);

  const {isDropping} = useDragDropTarget({
    ref: dndRef,
    targetId: folderKey,
    targetName: parentFolder?.name,
    options: {
      isUploadOnly: true,
    },
  });
  
  return (
    <DrawerLayout
      ref={drawer}
      drawerWidth={200}
      minSwipeDistance={3}
      edgeWidth={20}
      drawerType="front"
      drawerPosition="right"
      drawerLockMode={isDrawerEnabled ? 'unlocked' : 'locked-closed'}
      renderNavigationView={renderMenu}>
      <View ref={dndRef} style={styles.root}>
        {isFixedMenu &&
          <View style={styles.menu}>
            <Menu/>
          </View>
        }
        <View style={styles.content}>
          <Outlet context={[openMenu]}/>
          {Boolean(upload) &&
            <TransferUploads id={upload}/>
          }
          {download.length > 0 &&
            <TransferDownloads offset={!!upload}/>
          }
          {Boolean(preview) &&
            <FileLoader/>
          }
          <FolderPicker/>
        </View>
      </View>
      {isDropping && <DropFrame />}
    </DrawerLayout>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
  },
  menu: {
    width: 200,
  },
  folderTree: {
    width: 200,
  },
  content: {
    flex: 1,
  },
});
